<script lang="ts" setup>
import type { IProps } from './UiListWithIcons.types'

defineProps<IProps>()
</script>

<template>
  <ul
    :class="[
      'icon-list',
      `icon-list--${layout}`
    ]"
  >
    <li
      v-for="(item, index) in list"
      :key="index"
      :class="[
        'icon-list__item',
        { 'icon-list__item--number' : layout === 'layout-4' }
      ]"
    >
      <span class="icon-list__item_dot">
        <span />
      </span>
      
      <img
        v-if="item.icon"
        :src="item.icon.url"
        :alt="item.icon.alternative"
        :width="item.icon.width"
        :height="item.icon.height"
        class="icon-list__item_icon"
      >
      <div class="icon-list__item_header">
        <span
          :class="[
            'icon-list__item_text',
            { 'icon-list__item_text--number' : layout === 'layout-4' }
          ]"
          :title="`${index+1}`"
        >
          {{ item.text }}
        </span>

        <span
          v-if="item.subtext"
          class="icon-list__item_subtext"
        >
          {{ item.subtext }}
        </span>
      </div>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
.icon-list {
  $root: &;

  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  margin-top: 3.2rem;
  margin-bottom: 4rem;

  &--default {
    #{$root}__item {
      &:not(:last-of-type) {
        #{$root}__item_text {
          padding-bottom: 1.6rem;
          border-bottom: 1px solid $gray-300;

          @include atM {
            margin-bottom: 0;
            padding-bottom: 0;
            border-bottom: 0;
          }
        }
      }
    }
  }

  &--layout-1,
  &--layout-3,
  &--layout-4 {
    position: relative;
    gap: 3.2rem;

    #{$root}__item {
      position: relative;

      &:not(:last-of-type) {
        &:after {
          position: absolute;
          top: 30%;
          left: 1.6rem;
          display: block;
          width: 100%;
          height: calc(100% + 3.2rem);
          border-left: 0.1rem dashed $gray-500;
          content: '';
        }
      }

      @include atM {
        display: flex;
        flex-direction: column;
        max-width: 25rem;

        &:not(:last-of-type) {
          &:after {
            top: 1.6rem;
            left: 50%;
            width: calc(100% + 4rem);
            height: 100%;
            border-top: 0.1rem dashed $gray-500;
            border-left: none;

          }
        }
      }

      &--number {
        display: inline-block;

        @include atM {
          display: flex;
        }
      }

      &_text {
        padding-right: 0;

        @include atM {
          width: 100%;
          font-size: 2rem;
          line-height: 2.8rem;
          text-align: center;
        }

        @include atXL {
          font-size: 2.4rem;
          line-height: 3.2rem;
        }
      }

      &_dot {
        position: relative;
        z-index: 2;
        display: flex;
        justify-content: center;
        align-items: center;
        float: left;
        min-width: 3.2rem;
        width: 3.2rem;
        height: 3.2rem;
        margin: 1.2rem 2.4rem 1.2rem 0;
        background-color: $purple-100;
        border-radius: 50%;

        &:after {
          position: absolute;
          right: -2.4rem;
          display: inline-block;
          width: 2.4rem;
          border-top: 0.1rem dashed $gray-500;
          content: "";
        }

        span {
          display: block;
          width: 1.6rem;
          height: 1.6rem;
          background-color: $purple-400;
          border-radius: 50%;
        }

        @include atM {
          margin: 0 0 5rem 0;

          &:after {
            top: 3rem;
            right: 1.6rem;
            width: 0;
            height: 5rem;
            border-top: none;
            border-left: 0.1rem dashed $gray-500;
          }
        }
      }

      &_icon {
        width: 5.6rem;
        height: 5.6rem;

        @include atM {
          width: 7.2rem;
          height: 7.2rem;
          margin-right: 0;
          margin-bottom: 2.2rem;
        }
      }
    }

    @include atM {
      flex-direction: row;
      justify-content: space-between;
      margin-top: 6.2rem;
    }
  }

  &--layout-5 {
    display: flex;
    flex-direction: row;
    overflow: auto;

    &__item {
      flex: 1;
    }
  }

  &--layout-6 {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
  }

  @include atM {
    gap: 4rem;
    margin-bottom: 6.4rem;
  }

  &--layout-1 {

    @include atM {
      #{$root}__item {
        text-align: center;
      }
    }
  }

  &--layout-2 {
    flex-direction: column;
    gap: 2rem;

    @include atXXS {
      flex-direction: row;
    }

    #{$root}__item {
      flex-direction: column;
      align-items: center;
      padding: 2.4rem;
      border: 0.1rem solid $gray-300;
      border-radius: 1.6rem;

      &_icon {
        margin-right: 0;
        margin-bottom: 1.6rem;
      }

      &_text {
        padding: 0;
        text-align: center;
      }

      @include atM {
        width: calc(25% - 1.6rem);
        padding: 4rem;

        &_icon {
          margin-bottom: 3.2rem;
        }
      }
    }
  }

  &--layout-3 {
    gap: 0.8rem;

    #{$root}__item {

      &:not(:last-of-type) {
        &:after {
          height: calc(100% + 0.8rem);
        }
      }

      &_header {
        display: flex;
        flex-direction: column;
        width: 100%;
        padding: 2.4rem 2.4rem 1.2rem 2.4rem;
        background-color: $white;
        border-radius: 1.6rem;

        @include atM {
          padding: 2.4rem 3rem;
        }
      }

      &_text {
        text-align: left;

        @include atM {
          font-size: 1.8rem;
          line-height: 2.4rem;
        }
      }

      &_subtext {
        margin: 0;
        color: $text-300;
        font-size: 1.6rem;
        line-height: 2.4rem;
        text-align: left;
      }
    }

    @include atM {
      justify-content: flex-start;
      gap: 2rem;

      #{$root}__item {
        min-width: calc(25% - 1.6rem);
        width: calc(25% - 1.6rem);
      }
    }

  }

  &--layout-4 {
    
    @include atXS {
      max-width: 400px;
      margin-right: auto;
      margin-left: auto;
    }
    
    @include atM {
      max-width: 100%;
    }

    #{$root}__item {

      @include atXL {
        &:not(:last-of-type) {
          &:after {
            width: calc(100% + 10rem);
          }
        }
      }
    }
  }

  &__item {
    display: flex;
    font-weight: 500;
    font-size: 1.6rem;
    line-height: 2.4rem;

    @include atM {
      align-items: center;
      font-size: 1.8rem;
    }

    &_icon {
      display: inline-block;
      margin-right: 2.4rem;

      @include atM {
        margin-right: 4rem;
      }
    }

    &_text {
      padding-right: 3rem;

      &--number {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0.8rem 0;
        border: 0.1rem solid $purple-400;
        border-radius: 0.8rem;

        &:before {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 4rem;
          height: 4rem;
          margin-right: 1.4rem;
          color: $text-400;
          font-size: 2rem;
          line-height: 2.8rem;
          border: 0.1rem solid $gray-300;
          border-radius: 5rem;
          content: attr(title);
        }

        @include atM {
          &:before {
            width: 4.8rem;
            height: 4.8rem;
            font-size: 2.4rem;
            line-height: 3.2rem;
          }
        }
      }
    }

    &_subtext {
      display: inline-block;
      margin-top: 1.6rem;
      margin-left: 5.6rem;
      font-weight: 500;
      font-size: 1.6rem;
      line-height: 2.4rem;
      text-align: center;

      @include atM {
        margin-left: 0;
        font-size: 1.8rem;
      }
    }
  }
}
</style>
